import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import styles from "./CartDrawer.module.css";
import CloseIcon from "../CartIcons/CloseIcon";
import {
    applyCartDiscountFromCart,
    ATTRIBUTE_KEY_SHORTCODES,
    AvailableCartFooterLayouts,
    CUSTOM_HTML_POS_OPT,
    getFinalPrice,
    getPriceLabel,
    getProductsDynamicallyAndReturnNewProductsArray,
    isSpiritEmbassyShop,
    parseCTAWithIcon,
    updateBundleStats,
} from "../../Bundles/constants";
import { CartConfigInterface, CartData, CartDiscount } from "../CartTypes";
import DiscountBoxIcon from "../CartIcons/DiscountBoxIcon";
import AdditionalNotes from "../CartIcons/AdditionalNotes";
import { ProductObj, VariantsObj } from "../../Bundles/Types";
import { DefaultImageIcon, RenderDiscounts, RenderDiscountsBadges } from "./CartProducts";
import Button from "../../Bundles/BundlesComponents/Button";
import AdditionalProductComponent from "./AdditionalProductComponent";
import useCartDrawerLayoutHook from "../../Bundles/useCartDrawerLayoutHook";
import { debounce, handleCustomCheckout } from "../../utilities";
import { CustomSwatches } from "../../Bundles/BundlePreviewWrapper/BundlePreviewWrapper";
import useIsMobile from "../../../hooks/useIsMobile";
import DiscountIconBadge from "../CartIcons/DiscountIconBadge";
import Spinner from "../../../components/Icons/Spinner";
import { CustomHTMLWrapper } from "./AnnouncementComponent";
import ShippingProtectionCustomButton from "./ShippingProtectionCustomButton";
import { IS_METAL_BIRD_SHOP } from "../../constants";
import config from "../../../config";

export const LocalVariantSelector = ({
    variants = [],
    selected,
    cartConfig,
    handleChange = () => { },
    productCardIndex,
    realProduct,
}: {
    variants: VariantsObj[];
    selected: VariantsObj[];
    cartConfig: CartConfigInterface;
    handleChange: any;
    productCardIndex: number;
    realProduct: ProductObj;
}) => {
    const handleOptionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedOptionsArray =
            e.target.value
                ?.split(" / ")
                ?.map((val) => val.trim()) // Trim spaces around each value
                ?.filter((val) => val !== "") || [];

        const matchingVariant = findSelectedVariant(selectedOptionsArray);
        if (matchingVariant) {
            const curVar = selected[productCardIndex];
            const curSub = realProduct?.subscriptions?.find((ss) => ss?.id == curVar?.selling_plan);
            const updatedMatchingVariant = {
                ...matchingVariant,
                selling_plan: curVar?.selling_plan || "",
                copyPrice: matchingVariant.price,
                price: curSub
                    ? getFinalPrice(curSub?.discountType, curSub.discountValue, +matchingVariant.copyPrice).toString()
                    : matchingVariant.price,
            };

            const updatedSelectedVariants = [...selected];
            updatedSelectedVariants[productCardIndex] = updatedMatchingVariant; // <-- Update with productCardIndex
            handleChange(updatedSelectedVariants);
        } else {
            console._log("Matching variant not found!");
        }
    };

    const findSelectedVariant = (selectedOptionsArray: string[]) => {
        for (const variant of variants) {
            const hasAllSelectedOptions = selectedOptionsArray.every((selectedValue) =>
                variant.selectedOptions?.some((option) => option.value === selectedValue),
            );
            if (hasAllSelectedOptions) {
                return {
                    ...variant,
                    copyPrice: variant.price,
                };
            }
        }
        return null;
    };

    return (
        <div className={styles.optionsOuterContainer}>
            <div
                style={{
                    width: "100%",
                    maxWidth: "100%",
                    position: "relative",
                }}
            >
                <select
                    className={`${styles.select} ${styles.selectOverride}`}
                    name={"product_variants"}
                    style={{
                        width: "100%",
                        padding: "10px 20px",
                        fontFamily:
                            cartConfig?.cart_design?.cart_font_family == "Custom Store Font"
                                ? "inherit"
                                : cartConfig?.cart_design?.cart_font_family,
                    }}
                    id={selected[productCardIndex]?.title} // <-- Unique ID
                    onChange={(e) => handleOptionChange(e)}
                    value={selected[productCardIndex]?.title ? selected[productCardIndex]?.title : ""}
                >
                    {variants.map((value: any, idx: number) => (
                        <option
                            className={styles.option}
                            key={idx}
                            value={value.title}
                            disabled={value?.quantityAvailable <= 0 && !value?.availableForSale}
                        >
                            {value?.quantityAvailable <= 0 && !value?.availableForSale
                                ? `${value.title} - N/A`
                                : value.title}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
};

const SpecialBottomBoxesWrapper = ({ state, children }: { state: boolean; children: any }) => {
    return (
        <div className={`${styles.billSummaryOverlay} ${state ? styles.show : styles.hide}`} style={{ zIndex: 999999 }}>
            <div className={`${styles.billSummaryWrapper}`}>{children}</div>
        </div>
    );
};

const BillSummaryBox = ({
    handler,
    state,
    money_format,
    currency,
    cart,
    cartConfig,
    discountApplied,
}: {
    handler: Dispatch<SetStateAction<boolean>>;
    state: boolean;
    money_format: string;
    currency: string;
    cart: CartData;
    cartConfig: CartConfigInterface;
    discountApplied: CartDiscount[] | null;
}) => {
    const { billSummaryLayout } = useCartDrawerLayoutHook({ config: cartConfig });
    const [isShippingFree, setIsShippingFree] = useState(false);
    useEffect(() => {
        if (!cartConfig?.reward) {
            return;
        }
        const { tiers, status, reward_basis } = cartConfig.reward;
        if (status && tiers?.length && !!tiers?.some((tt) => tt?.type == "free_shipping")) {
            const index = tiers?.findIndex((tt) => tt?.type == "free_shipping");
            const compareWith = reward_basis == "cart_total" ? cart?.total_price / 100 : cart?.item_count;
            setIsShippingFree(compareWith >= tiers?.[index]?.count);
        } else {
            setIsShippingFree(false);
        }
    }, [cartConfig, cart?.total_price, cart?.item_count, cartConfig?.reward]);

    return (
        <SpecialBottomBoxesWrapper state={state}>
            <>
                <div
                    className={styles.billSummaryTitle}
                    style={{
                        fontWeight: billSummaryLayout.title,
                    }}
                >
                    {cartConfig?.bill_summary_text}
                    <span
                        className={styles.closeBillSummary}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handler(false);
                        }}
                    >
                        <CloseIcon size={20} color={"#202020"} />
                    </span>
                </div>
                <div className={styles.billSummaryBox}>
                    <div className={styles.billSummaryRow}>
                        <span
                            className={styles.label}
                            style={{
                                fontWeight: billSummaryLayout.labelFontWeight,
                            }}
                        >
                            {cartConfig?.item_total_text}
                        </span>
                        <span className={styles.val}>
                            <span
                                className={styles.primaryVal}
                                style={{
                                    fontWeight: billSummaryLayout.fontWeight,
                                }}
                            >
                                {getPriceLabel((cart?.original_total_price || 0) / 100, money_format, currency, false)}
                            </span>
                        </span>
                    </div>
                    {isSpiritEmbassyShop() && cart?.original_total_price - cart?.total_price > 0 && (
                        <div className={styles.billSummaryRow}>
                            <span
                                className={styles.label}
                                style={{
                                    fontWeight: billSummaryLayout.labelFontWeight,
                                }}
                            >
                                Discount
                            </span>
                            <span className={styles.val}>
                                <span
                                    className={styles.primaryVal}
                                    style={{
                                        fontWeight: billSummaryLayout.fontWeight,
                                    }}
                                >
                                    -
                                    {getPriceLabel(
                                        (cart?.original_total_price - cart?.total_price || 0) / 100,
                                        money_format,
                                        currency,
                                        false,
                                    )}
                                </span>
                            </span>
                        </div>
                    )}
                    <div className={styles.billSummaryRow}>
                        <span
                            className={styles.label}
                            style={{
                                fontWeight: billSummaryLayout.labelFontWeight,
                            }}
                        >
                            {cartConfig?.shipping_text}
                        </span>
                        <span className={styles.val}>
                            <span
                                className={styles.primaryVal}
                                style={{
                                    fontWeight: billSummaryLayout.subFontWeight,
                                }}
                            >
                                {isShippingFree ? "Free" : cartConfig?.shipping_message_text}
                            </span>
                        </span>
                    </div>
                    {!!discountApplied?.length && (
                        <div className={styles.billSummaryRow}>
                            <span
                                className={styles.label}
                                style={{
                                    fontWeight: billSummaryLayout.labelFontWeight,
                                }}
                            >
                                {cartConfig?.cart_discounts_text}
                            </span>
                            <span className={styles.val}>
                                <span
                                    className={styles.primaryVal}
                                    style={{
                                        width: "auto",
                                        fontWeight: billSummaryLayout.subFontWeight,
                                        display: "flex",
                                        flexWrap: "wrap",
                                        flexGrow: 1,
                                    }}
                                >
                                    <RenderDiscounts arr={discountApplied} />
                                </span>
                            </span>
                        </div>
                    )}

                    <span className={styles.billSummaryDivider}></span>
                    <div className={styles.billSummaryGrandTotal}>
                        <span
                            className={styles.label}
                            style={{
                                fontWeight: billSummaryLayout.fontWeight,
                            }}
                        >
                            {cartConfig?.grand_total_text}
                        </span>
                        <span
                            className={styles.val}
                            style={{
                                fontWeight: billSummaryLayout.fontWeight,
                            }}
                        >
                            {getPriceLabel((cart?.total_price || 0) / 100, money_format, currency, false)}
                        </span>
                    </div>
                </div>
            </>
        </SpecialBottomBoxesWrapper>
    );
};

const DiscountBox = ({
    handler,
    sft,
    state,
    setState,
    cart,
    cartConfig,
    standAlone,
    setErrors,
    // discountApplied,
    handleFetchCart,
}: {
    handler: Dispatch<SetStateAction<boolean>>;
    state: boolean;
    cartConfig: CartConfigInterface;
    sft: string;
    cart: any;
    setState: any;
    standAlone: boolean;
    discountApplied: CartDiscount[] | null;
    handleFetchCart: any;
    setErrors: Dispatch<SetStateAction<null | { isError: boolean; msg: string }>>;
}) => {
    const [applyingCode, setApplyingCode] = useState(false);
    const [code, setCode] = useState("");

    // useEffect(() => {
    //     if (discountApplied?.length) {
    //         setCode(discountApplied?.filter((dis) => dis.type == "discount_code")[0]?.title || "");
    //     }
    // }, [discountApplied]);

    return (
        <SpecialBottomBoxesWrapper state={state}>
            <>
                <div
                    className={styles.billSummaryTitle}
                    style={{
                        color: cartConfig?.discount_design?.title_color,
                    }}
                >
                    <span style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 5 }}>
                        <DiscountBoxIcon size={16} color={cartConfig?.discount_design?.title_color} />{" "}
                        <span dangerouslySetInnerHTML={{ __html: cartConfig?.discount?.title }}></span>
                    </span>
                    <span
                        className={styles.closeBillSummary}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handler(false);
                        }}
                    >
                        <CloseIcon size={20} color={"#202020"} />
                    </span>
                </div>
                <div style={{ display: "flex", justifyContent: "stretch", alignItems: "stretch", gap: 20 }}>
                    <input
                        className={styles.codeBoxInput}
                        style={{
                            borderRadius: cartConfig?.cart_design?.footer_button_radius,
                            border: `1px solid ${cartConfig?.cart_design?.footer_button_bg_color}`,
                        }}
                        placeholder={cartConfig?.discount?.placeholder || ""}
                        onBlur={() => {
                            setCode((ps) => ps?.toUpperCase());
                        }}
                        onChange={(e: any) => {
                            setCode(e?.target?.value);
                        }}
                        value={code}
                    />
                    <Button
                        className={`${styles.checkoutButton}  ${applyingCode && styles.checkoutButtonLoading}`}
                        onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setApplyingCode(true);
                            const codes = cart?.discount_codes?.map((dc: any) => dc.code) || [];
                            const res = await applyCartDiscountFromCart(sft, cart?.token, [code, ...codes], standAlone);
                            await handleFetchCart();
                            if (
                                (res &&
                                    res?.cart?.discountCodes?.length &&
                                    res?.cart?.discountCodes?.[0]?.applicable) ||
                                !code.length
                            ) {
                                setErrors({
                                    isError: false,
                                    msg: code?.length ? "Code applied successfully" : "Code removed successfully",
                                });
                                setState(false);
                            } else {
                                setErrors({ isError: true, msg: "Invalid code" });
                            }

                            setApplyingCode(false);
                        }}
                        title={cartConfig?.discount?.cta_text}
                        spinnerSize={16}
                        style={{
                            height: 30,
                            minHeight: 30,
                            padding: 20,
                            width: "30%",
                            borderRadius: cartConfig?.cart_design?.footer_button_radius,
                            // @ts-expect-error hack
                            "--buttonBg": cartConfig?.discount_design?.cta_bg_color,
                            "--buttonText": cartConfig?.discount_design?.cta_text_color,
                            "--buttonHoverBg": cartConfig?.discount_design?.cta_bg_hover_color,
                            "--buttonHoverText": cartConfig?.discount_design?.cta_text_hover_color,
                            "--buttonBgBorder": cartConfig?.cart_design?.footer_button_border_color,
                            "--buttonHoverBgBorder": cartConfig?.cart_design?.footer_button_hover_border_color,
                        }}
                        loading={applyingCode}
                        inheritLoadingColor={true}
                    />
                </div>
            </>
        </SpecialBottomBoxesWrapper>
    );
};

const AdditionalNoteBox = ({
    handler,
    state,
    cartConfig,
    updateCartData,
    cart,
}: {
    cart: CartData;
    handler: Dispatch<SetStateAction<boolean>>;
    state: boolean;
    cartConfig: CartConfigInterface;
    updateCartData?: ((payload: any, key: string) => Promise<void>) | (() => void);
}) => {
    const _updateCart = useCallback(
        debounce((additionalNote) => {
            updateCartData && updateCartData({ note: additionalNote }, "Additional Notes ");
        }, 500),
        [],
    );

    const [additionalNote, setAdditionalNote] = useState(cart?.note || "");
    return (
        <SpecialBottomBoxesWrapper state={state}>
            <>
                <div
                    className={styles.billSummaryTitle}
                    style={{
                        color: cartConfig?.additional_note_design?.title_color,
                    }}
                >
                    <span style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 5 }}>
                        <AdditionalNotes size={16} color={cartConfig?.additional_note_design?.title_color} />{" "}
                        <span dangerouslySetInnerHTML={{ __html: cartConfig?.additional_note?.title }}></span>
                    </span>
                    <span
                        className={styles.closeBillSummary}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handler(false);
                        }}
                    >
                        <CloseIcon size={20} color={"#202020"} />
                    </span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                    <textarea
                        className={styles.codeBoxInput}
                        style={{
                            borderRadius: 4,
                            padding: 10,
                            minHeight: 80,
                            maxHeight: 100,
                            maxWidth: "100%",
                            fontFamily: cartConfig?.cart_design?.cart_font_family,
                            border: `1px solid ${cartConfig?.cart_design?.footer_button_bg_color}`,
                        }}
                        placeholder={cartConfig?.additional_note?.placeholder}
                        onChange={(e: any) => {
                            setAdditionalNote(e?.target?.value);
                        }}
                        value={additionalNote}
                    />
                </div>

                <Button
                    title={cartConfig.additional_note?.cta_text || "Save"}
                    onClick={() => {
                        _updateCart(additionalNote);
                        handler(false);
                    }}
                    disabledCta={false}
                    style={{
                        height: 30,
                        minHeight: 30,
                        padding: 18,
                        marginTop: 10,
                        width: "100%",
                        fontSize: 14,
                        borderRadius: cartConfig?.cart_design?.footer_button_radius,
                        backgroundColor: cartConfig?.cart_design?.footer_button_bg_color,
                        color: cartConfig?.cart_design?.footer_button_text_color,
                        borderColor: cartConfig?.cart_design?.footer_button_border_color,
                    }}
                ></Button>
            </>
        </SpecialBottomBoxesWrapper>
    );
};

export const AddProductToCartBox = ({
    cartConfig,
    product,
    setVariant,
    money_format,
    currency,
    addToCartFromCart,
    cart,
    standAlone,
    isRewardFreeGift = false,
    allowOnlySingleFG = false,
    propertyName,
    triggerRewardExtraFxn,
    triggerRewardExtraFxn2,
    rewardKey,
}: {
    cartConfig: CartConfigInterface;
    product: ProductObj;
    setVariant: any;
    money_format: string;
    currency: string;
    addToCartFromCart: any;
    cart: CartData;
    standAlone: boolean;
    isRewardFreeGift: boolean;
    allowOnlySingleFG?: boolean;
    propertyName?: string;
    triggerRewardExtraFxn?: () => void;
    triggerRewardExtraFxn2?: () => void;
    rewardKey?: string;
}) => {
    const [selectedVariants, setSelectedVariants] = useState(product.selectedVariants);
    const [adding, setAdding] = useState(false);
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setAdding(false);
        setSelectedVariants([]);
        setVariant({});
    }, [cart?.items]);
    return (
        <SpecialBottomBoxesWrapper state={!!product?.id}>
            <>
                <div className={styles.billSummaryTitle}>
                    <span
                        className={styles.closeBillSummary}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setVariant({});
                        }}
                    >
                        <CloseIcon size={20} color={"#202020"} />
                    </span>
                </div>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        marginBottom: 10,
                        gap: 10,
                    }}
                >
                    <div
                        className={styles.upsellItemImageWrapper}
                        style={{
                            border: "1px solid #DEDEDE",
                            borderRadius: 7,
                            background: "#ffffff",
                            width: 70,
                            maxWidth: 70,
                            minWidth: 70,
                            margin: 0,
                        }}
                    >
                        {selectedVariants[0]?.image?.originalSrc ? (
                            <img
                                src={selectedVariants[0]?.image?.originalSrc}
                                alt={product?.handle}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    padding: 2,
                                    objectFit: "cover",
                                    objectPosition: "center",
                                    borderRadius: 8,
                                }}
                            />
                        ) : (
                            <DefaultImageIcon w={"100%"} h={"100%"} />
                        )}
                    </div>
                    <div className={styles.upsellItemDetails} style={{ gap: 8, flexGrow: 1 }}>
                        <div
                            className={styles.upsellItemTitle}
                            style={{
                                minHeight: "max-content",
                            }}
                            title={product?.title}
                        >
                            {product?.title}
                        </div>
                        <div className={styles.upsellItemPriceWrapper}>
                            <span className={styles.upsellItemPrice}>
                                {getPriceLabel(selectedVariants[0]?.price, money_format, currency, true)}
                            </span>
                            {+selectedVariants[0]?.price != +selectedVariants[0]?.compareAtPrice && (
                                <span className={styles.upsellItemPriceStrike}>
                                    {getPriceLabel(selectedVariants[0]?.compareAtPrice, money_format, currency, true)}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                {selectedVariants.map((_pd: any, i: number) => {
                    return cartConfig?.cart_settings?.show_swatches ? (
                        <CustomSwatches
                            key={i}
                            variants={product.variants}
                            handleChange={setSelectedVariants}
                            selected={selectedVariants}
                            options={product.options || []}
                            productCardIndex={i}
                            standAlone={standAlone}
                            metafield={product.metafield}
                            bundlesDesign={cartConfig?.cart_design}
                            upsellDesign={cartConfig?.upsell_design}
                            bundleConfig={{}}
                            isCart={true}
                        />
                    ) : (
                        <LocalVariantSelector
                            key={i}
                            variants={product.variants}
                            handleChange={setSelectedVariants}
                            selected={selectedVariants}
                            productCardIndex={i}
                            realProduct={_pd}
                            cartConfig={cartConfig}
                        />
                    );
                })}

                <div style={{ display: "flex", flexDirection: "column", gap: 20, marginTop: 5 }}>
                    <Button
                        className={`${styles.upsellAddToCart} ${adding && styles.upsellAddToCartLoading}`}
                        title={"Add to cart"}
                        onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setAdding(true);

                            if (propertyName === ATTRIBUTE_KEY_SHORTCODES.__triggerRewards) {
                                // @ts-expect-error always comes in trigger rewards
                                triggerRewardExtraFxn();
                                await addToCartFromCart(
                                    [{ ...product, selectedVariants: selectedVariants }],
                                    {
                                        [ATTRIBUTE_KEY_SHORTCODES.__triggerRewards]: { rewardKey },
                                    },
                                    true,
                                );
                                // @ts-expect-error always comes in trigger rewards
                                triggerRewardExtraFxn2();
                                return;
                            }

                            if (allowOnlySingleFG) {
                                // this is being done for metal bird shop
                                const pIds = cartConfig?.metal_bird_config?.free_gifts.map((p) =>
                                    p.id?.split("/")?.pop(),
                                );
                                const anyFgInCart = cart.items.some(
                                    (item: any) =>
                                        pIds?.includes(item.product_id?.toString()) &&
                                        item.properties?.[ATTRIBUTE_KEY_SHORTCODES.__kbd]?.[
                                        ATTRIBUTE_KEY_SHORTCODES.__freeGiftRewards
                                        ] != null,
                                );
                                if (anyFgInCart) {
                                    const freeGiftCartItems = cart.items.filter(
                                        (item: any) =>
                                            pIds?.includes(item.product_id?.toString()) &&
                                            item.properties?.[ATTRIBUTE_KEY_SHORTCODES.__kbd]?.[
                                            ATTRIBUTE_KEY_SHORTCODES.__freeGiftRewards
                                            ] != null,
                                    );

                                    for (const item of freeGiftCartItems) {
                                        await window.originalFetch("/cart/change.js", {
                                            method: "POST",
                                            headers: { "Content-Type": "application/json" },
                                            body: JSON.stringify({ id: item.key, quantity: 0 }),
                                        });
                                    }
                                }
                            }

                            if (!isRewardFreeGift && selectedVariants?.[0]?.id) {
                                updateBundleStats({
                                    id: null,
                                    key: "views",
                                    view_type: 2,
                                    shop: window.kefi_bundles?.shop,
                                    appName: config.appName,
                                    product_ids: selectedVariants[0].id,
                                });
                            }

                            addToCartFromCart(
                                [{ ...product, selectedVariants: selectedVariants }],
                                {
                                    ...(isRewardFreeGift
                                        ? {
                                            [ATTRIBUTE_KEY_SHORTCODES.__freeGiftRewards]: {},
                                        }
                                        : {}),
                                    [ATTRIBUTE_KEY_SHORTCODES.__upsell]: {
                                        [ATTRIBUTE_KEY_SHORTCODES.cart_upsell_offer_id]: product?.cartUpsellId ?? "",
                                    },
                                },
                                true,
                            );
                        }}
                        spinnerSize={16}
                        style={{
                            height: 30,
                            minHeight: 30,
                            padding: 20,
                            borderRadius: cartConfig?.cart_design?.footer_button_radius,
                            // @ts-expect-error hack
                            "--buttonBg": cartConfig?.cart_design?.footer_button_bg_color,
                            "--buttonText": cartConfig?.cart_design?.footer_button_text_color,
                            "--buttonHoverBg": cartConfig?.cart_design?.footer_button_bg_hover_color,
                            "--buttonHoverText": cartConfig?.cart_design?.footer_button_text_hover_color,
                            "--buttonBgBorder": cartConfig?.cart_design?.footer_button_border_color,
                            "--buttonHoverBgBorder": cartConfig?.cart_design?.footer_button_hover_border_color,
                        }}
                        loading={adding}
                        inheritLoadingColor={true}
                    />
                </div>
            </>
        </SpecialBottomBoxesWrapper>
    );
};

const CheckoutButtonDisableOverlap = ({
    active,
    onClick
}: {
    active: boolean
    onClick: () => void
}) => {
    return <div
        onClick={onClick}
        style={active ? {
            position: "absolute",
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            zIndex: 1111,
        } : {
            display: "none"
        }}
    />

};

const CartFooter = ({
    cart,
    cartConfig,
    money_format,
    currency,
    setShowCartDrawer,
    addProductToCart,
    setAddProductToCart,
    addToCartFromCart,
    sft,
    standAlone,
    setErrors,
    handleFetchCart,
    updateCartData,
    handleAdditionalProductAdd,
    changeCartItem = () => { },
    isUpdating = false,
    setIsUpdating = () => { },
    customShippingProtection = false,
}: {
    standAlone: boolean;
    cartConfig: CartConfigInterface;
    cart: CartData;
    currency: string;
    money_format: string;
    setShowCartDrawer: Dispatch<SetStateAction<boolean>>;
    addProductToCart: any;
    setAddProductToCart: Dispatch<SetStateAction<any>>;
    addToCartFromCart: any;
    sft: string;
    setErrors: Dispatch<SetStateAction<null | { isError: boolean; msg: string }>>;
    handleFetchCart: any;
    updateCartData?: ((payload: any, key: string) => Promise<void>) | (() => void);
    handleAdditionalProductAdd: ({
        state,
        additionalProduct,
    }: {
        state: boolean;
        additionalProduct: ProductObj;
        checkoutWithShipping?: boolean;
    }) => Promise<void>;

    changeCartItem?: ((payload: any) => Promise<void>) | (() => void);
    isUpdating?: boolean;
    setIsUpdating?: Dispatch<SetStateAction<boolean>>;
    customShippingProtection?: boolean;
}) => {
    const handleCheckout = (cartPage = false) => {
        if (cartConfig?.terms_and_conditions?.status && !isTermsChecked) {
            setTermsError(true);
            return;
        }

        try {
            handleCustomCheckout();
            setTimeout(() => {
                window.openMyCart(false);
                setIsUpdating(false);
            }, 1000);
        } catch (error) {
            setTimeout(() => {
                window.openMyCart(false);
                setIsUpdating(false);
            }, 1000);
            window.location = (cartPage ? window.kefi_bundles.routes.cart_url : "/checkout/") as unknown as Location;
        }
    };
    const { cartGlobal, cartFooterDiscountNotes, cartFooter } = useCartDrawerLayoutHook({
        config: cartConfig,
    });
    const [discountApplied, setDiscountApplied] = useState<null | CartDiscount[]>([]);
    const [showDiscountBox, setShowDiscountBox] = useState(false);
    const [showBillSummary, setShowBillSummary] = useState(false);
    const [showAdditionalNote, setShowAdditionalNote] = useState(false);
    const [isTermsChecked, setIsTermsChecked] = useState(false);
    const [termsError, setTermsError] = useState(false);
    const [cartFooterFeatures, setCartFooterFeatures] = useState<any>([]);
    const [removingDiscountCode, setRemovingDiscountCode] = useState(-1);
    const { isMobile } = useIsMobile();
    const [currentProduct, setCurrentProduct] = useState<ProductObj | null>(!standAlone ? ({} as ProductObj) : null);

    const selectedBadges = cartConfig?.trust_badges?.badges.filter((badge) => badge?.selected);

    useEffect(() => {
        if (!standAlone) return;
        if (customShippingProtection && currentProduct && !isUpdating) {
            const lineIndex = cart?.items?.findIndex(
                (pd) => !!currentProduct?.id?.includes(pd.product_id as unknown as string),
            );

            if (lineIndex >= 0) {
                setIsUpdating(true);
                changeCartItem({ line: lineIndex + 1, quantity: 0 });
                window.sessionStorage.removeItem("shipping_protection_removed");
            }
        }
    }, [cart?.items, customShippingProtection, currentProduct, standAlone]);

    useEffect(() => {
        const arr = [];
        if (cartConfig?.additional_note?.status) {
            arr.push({
                label: cartConfig?.additional_note?.Additional_notes_title_text || "Additional Notes",
                icon: <AdditionalNotes color={cartConfig?.additional_note_design?.icon_color} />,
                handler: () => setShowAdditionalNote(true),
                key: "additional_note",
                color: cartConfig?.additional_note_design?.icon_color,
            });
        }
        if (cartConfig?.discount?.status) {
            arr.push({
                label: cartConfig?.discount?.label_text || "Apply Discount Code",
                icon: <DiscountBoxIcon color={cartConfig?.discount_design?.icon_color} />,
                handler: () => setShowDiscountBox(true),
                key: "discount",
                color: cartConfig?.discount_design?.icon_color,
            });
        }
        setCartFooterFeatures(arr);
    }, [
        cartConfig?.additional_note,
        cartConfig?.discount,
        cartConfig?.additional_note_design,
        cartConfig?.discount_design,
    ]);

    useEffect(() => {
        if (cart?.cart_level_discount_applications?.length) {
            console._log("discountApplied", cart?.cart_level_discount_applications);
            setDiscountApplied(cart?.cart_level_discount_applications);
        } else {
            if (standAlone) {
                setDiscountApplied([]);
            } else {
                // @ts-expect-error hack
                setDiscountApplied([{ title: "10% off", total_allocated_amount: 17600 }]);
            }
        }
        setRemovingDiscountCode(-1);
    }, [cart]);

    useEffect(() => {
        const ele = document.getElementById("kcdFooter");
        const ele2 = document.getElementById("kcdFooterMsg");
        if (cartConfig?.cart_settings?.show_text_below_add_to_cart && ele && ele2) {
            if (ele && ele2) {
                const dimensions = ele2.getBoundingClientRect();
                ele.style.marginBottom = dimensions.height + 6 + "px";
            }
        } else if (ele && ele2) {
            ele.style.marginBottom = "0px";
        }
    }, [cartConfig?.cart_settings?.show_text_below_add_to_cart, cartConfig?.cart_settings?.text_below_add_to_cart]);

    useEffect(() => {
        if (!standAlone) return;
        const getProductInfo = async (pId: null | string = null) => {
            if (!pId) {
                return;
            }
            const updateProduct = await getProductsDynamicallyAndReturnNewProductsArray([{ id: pId }], sft, true);
            setCurrentProduct(updateProduct[0]);
        };
        if (cartConfig?.additional_product?.product_type == "shipping_protection") {
            getProductInfo(cartConfig?.additional_product?.shipping_protection_id);
        } else if (cartConfig?.additional_product?.product_type == "gift_wrapping") {
            getProductInfo(cartConfig?.additional_product?.gift_wrapping_id);
        } else {
            setCurrentProduct(null);
        }
    }, [cartConfig?.additional_product?.product_type]);

    const CheckoutStyles = (
        <style>{`
            .kefiCartCheckoutButton {
                flex-grow: 1;
                width: 100%;
                font-size: ${cartConfig?.cart_design?.footer_button_cta_text_size}px;
                font-weight: ${cartConfig?.cart_design?.footer_button_cta_text_style?.split(" ")[0]};
                font-style: ${cartConfig?.cart_design?.footer_button_cta_text_style?.split(" ")[1]};
                border-radius: ${cartConfig?.cart_design?.footer_button_radius}px;
                --buttonBg: ${cartConfig?.cart_design?.footer_button_bg_color};
                --buttonText: ${cartConfig?.cart_design?.footer_button_text_color};
                --buttonHoverBg: ${cartConfig?.cart_design?.footer_button_bg_hover_color};
                --buttonHoverText: ${cartConfig?.cart_design?.footer_button_text_hover_color};
                --buttonBgBorder: ${cartConfig?.cart_design?.footer_button_border_color};
                --buttonHoverBgBorder: ${cartConfig?.cart_design?.footer_button_hover_border_color};
                padding: ${cartConfig?.checkout_cta_text?.includes("{icon}") ? 8 : 10}px;
            }
        `}</style>
    );
    return (
        <div
            className={styles.cartFooterWrapper}
            id="cartFooterWrapper" // shouldn't be changed
            style={{
                background: cartConfig?.cart_design?.footer_bg_color,
                position: cartConfig?.cart_settings?.sticky_footer ? "sticky" : undefined,
                borderTop: cartGlobal.removeBorders || !cartFooterFeatures?.length ? "none" : "2px solid #e5e7eb",
            }}
        >
            {cart?.items?.length > 0 && (
                <div
                    className={styles.additionalProductContainer}
                    id="additionalProductContainer" // shouldn't be changed
                    style={{ background: "transparent" }}
                >
                    <AdditionalProductComponent
                        cartConfig={cartConfig}
                        handleAdditionalProductAdd={handleAdditionalProductAdd}
                        sft={sft}
                        standAlone={standAlone}
                        money_format={money_format}
                        cartLines={cart?.items}
                        addToCartFromCart={addToCartFromCart}
                        customShippingProtection={customShippingProtection}
                        changeCartItem={changeCartItem}
                    />
                </div>
            )}
            {!!cartFooterFeatures?.length && (
                <div
                    className={`${cartGlobal.removeBorders
                        ? styles.cartFooterBoxesOptions
                        : styles.cartFooterBoxesOptionsWithBorder
                        }`}
                >
                    {cartFooterFeatures.map((feat: any, idx: number) => (
                        <>
                            {idx > 0 && <span className={styles.featureDivider}></span>}
                            <div
                                className={styles.featureItem}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    feat.handler();
                                }}
                                style={{
                                    color: feat?.color,
                                    fontWeight: cartFooterDiscountNotes.fontWeight || 550,
                                }}
                            >
                                <div className={styles.featureItem}>
                                    <div>{feat?.icon}</div>
                                    <span className={styles.featureLabel}>{feat?.label}</span>
                                </div>
                            </div>
                        </>
                    ))}
                </div>
            )}
            {!!cart?.discount_codes?.length && (
                <div className={styles.cartLevelDiscountCodes}>
                    {cart?.discount_codes.map((dc, dcIdx) => {
                        return (
                            <>
                                {dc?.applicable && (
                                    <div
                                        key={"idx + dis?.title"}
                                        className={styles.cartLevelDiscountCode}
                                        style={{
                                            background: cartConfig?.discount_design?.code_bg_color,
                                            color: cartConfig?.discount_design?.code_text_color,
                                        }}
                                    >
                                        {removingDiscountCode == dcIdx && (
                                            <span className={styles.cartLevelDiscountCodeLoader}>
                                                <Spinner
                                                    size={20}
                                                    color={cartConfig?.discount_design?.code_text_color}
                                                />
                                            </span>
                                        )}
                                        <span className={styles.discountCodeLabel}>
                                            <span
                                                style={{
                                                    position: "relative",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                }}
                                            >
                                                <DiscountIconBadge
                                                    size={13}
                                                    color={cartConfig?.discount_design?.code_text_color}
                                                />
                                            </span>
                                            {dc?.code}
                                        </span>
                                        <span
                                            className={styles.discountCodeRemoveIcon}
                                            onClick={async (e) => {
                                                e?.preventDefault();
                                                e.stopPropagation();
                                                setRemovingDiscountCode(dcIdx);
                                                const codes =
                                                    cart?.discount_codes
                                                        ?.map((dc: any) => dc.code)
                                                        ?.filter((code) => code != dc?.code) || [];
                                                const res = await applyCartDiscountFromCart(
                                                    sft,
                                                    cart?.token,
                                                    [...codes],
                                                    standAlone,
                                                );
                                                await handleFetchCart();
                                                if (res && res?.cart?.discountCodes) {
                                                    setErrors({
                                                        isError: false,
                                                        msg: "Code removed successfully",
                                                    });
                                                }
                                                setRemovingDiscountCode(-1);
                                            }}
                                        >
                                            <CloseIcon />
                                        </span>
                                    </div>
                                )}
                            </>
                        );
                    })}
                </div>
            )}
            {!!discountApplied?.length && (
                <div className={styles.cartLevelDiscounts}>
                    {discountApplied.map((dis, idx) => {
                        const amt = getPriceLabel(
                            dis?.total_allocated_amount / 100,
                            money_format,
                            currency,
                            false,
                            "string",
                        );
                        const textTemplate = cartConfig?.order_discount_message_text || "";
                        const parts = textTemplate.split(/(\{amount_saved\}|\{offer\})/g);

                        return (
                            <div
                                key={idx + dis?.title}
                                className={styles.cartLevelDiscountItem}
                                style={{
                                    color: cartConfig?.cart_design.cart_primary_color,
                                }}
                            >
                                <DiscountBoxIcon color={cartConfig?.cart_design.cart_primary_color} />
                                <span>
                                    {parts.map((part, i) => {
                                        if (part === "{amount_saved}")
                                            return <span key={i} dangerouslySetInnerHTML={{ __html: amt ?? "" }} />;
                                        if (part === "{offer}")
                                            return <React.Fragment key={i}>{dis?.title}</React.Fragment>;
                                        return <React.Fragment key={i}>{part}</React.Fragment>;
                                    })}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
            <CustomHTMLWrapper cartConfig={cartConfig} currentPosition={CUSTOM_HTML_POS_OPT.ABOVE_CHECKOUT} />

            {cartConfig?.selected_drawer_footer_layout == AvailableCartFooterLayouts.HORIZONTAL &&
                customShippingProtection && (
                    <div
                        className={styles.shippingProtectionWrapper}
                        style={{
                            background: cartConfig?.additional_product_design?.additional_product_card_bg,
                            paddingRight: 0,
                            paddingLeft: 0,
                            border: "none",
                        }}
                    >
                        <div className={styles.shippingProtectionLeft}>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <span
                                    className={styles.shippingProtectionTitle}
                                    style={{
                                        fontSize: cartConfig?.additional_product_design?.additional_product_title_size,
                                        color: cartConfig?.additional_product_design?.additional_product_title_color,
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            cartConfig?.additional_product?.shipping_protection_title ||
                                            "Shipping protection",
                                    }}
                                ></span>

                                <span
                                    className={styles.shippingProtectionSubtext}
                                    style={{
                                        fontSize:
                                            cartConfig?.additional_product_design?.additional_product_description_size,
                                        color: cartConfig?.additional_product_design
                                            ?.additional_product_description_color,
                                        fontWeight:
                                            cartConfig?.additional_product_design?.additional_product_description_style?.split(
                                                " ",
                                            )[0],
                                        fontStyle:
                                            cartConfig?.additional_product_design?.additional_product_description_style?.split(
                                                " ",
                                            )[1],
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: cartConfig?.additional_product?.shipping_protection_subtext?.replace(
                                            "{price}",
                                            money_format
                                                .replace(
                                                    /\{\{.*?\}\}/g,
                                                    (
                                                        cartConfig?.additional_product?.shipping_protection_price *
                                                        (window.kefi_bundles.localization.rate || 1)
                                                    )?.toFixed(2),
                                                )
                                                ?.toString(),
                                        ),
                                    }}
                                ></span>
                            </div>
                        </div>

                        <div className={styles.shippingProtectionRight}>
                            <span
                                className={styles.shippingProtectionPrice}
                                style={{
                                    fontWeight: "bold",
                                    fontSize: cartConfig?.additional_product_design?.additional_product_title_size,
                                    color: cartConfig?.additional_product_design?.additional_product_title_color,
                                }}
                            >
                                {money_format.replace(
                                    /\{\{.*?\}\}/g,
                                    (
                                        cartConfig?.additional_product?.shipping_protection_price *
                                        (window.kefi_bundles.localization.rate || 1)
                                    )?.toFixed(2),
                                )}
                            </span>
                        </div>
                    </div>
                )}

            {cartConfig?.terms_and_conditions?.status && (
                <div className={styles.termsAndConditionsWrapper}>
                    <div className={styles.termsAndConditionsCheckboxWrapper}>
                        <input
                            type="checkbox"
                            checked={isTermsChecked}
                            onChange={(e) => {
                                setIsTermsChecked(e.target.checked);
                                if (e.target.checked) setTermsError(false);
                            }}
                            id="terms_and_conditions_checkbox"
                            style={{
                                accentColor: cartConfig.cart_design?.cart_primary_color,
                            }}
                            className={styles.termsAndConditionsCheckbox}
                        />
                        <label
                            htmlFor="terms_and_conditions_checkbox"
                            dangerouslySetInnerHTML={{ __html: cartConfig.terms_and_conditions.text }}
                            style={{
                                color: cartConfig.terms_and_conditions_design?.text_color,
                                // @ts-ignore colorVariable
                                "--link-color": cartConfig.terms_and_conditions_design?.link_color,
                            }}
                            className={styles.termsAndConditionsLabel}
                        />
                    </div>
                    {termsError && (
                        <div className={styles.termsAndConditionsError}>
                            {cartConfig.terms_and_conditions.required_text}
                        </div>
                    )}
                </div>
            )}

            <div
                className={styles.ctaPriceRow}
                id={"kcdFooter"}
                style={{
                    ...(cartConfig?.selected_drawer_footer_layout == AvailableCartFooterLayouts.VERTICAL && {
                        flexDirection: "column",
                        width: "100%",
                        gap: 10,
                    }),
                    marginBottom: cartConfig?.cart_settings?.show_text_below_add_to_cart ? 30 : 0,
                }}
            >
                {customShippingProtection && isUpdating && (
                    <span className={styles.cartLevelDiscountCodeLoader}>
                        <Spinner size={35} color={cartConfig?.discount_design?.code_text_color} />
                    </span>
                )}
                <div
                    style={{
                        ...(cartConfig?.selected_drawer_footer_layout == AvailableCartFooterLayouts.VERTICAL && {
                            display: "flex",
                            flexDirection: "row-reverse",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                        }),
                    }}
                >
                    <div
                        className={styles.subtotalAmount}
                        style={{
                            fontWeight: cartFooter?.fontWeight,
                            ...(cartConfig?.selected_drawer_footer_layout == AvailableCartFooterLayouts.VERTICAL && {
                                flexDirection: "row-reverse",
                                alignItems: "center",
                            }),
                        }}
                    >
                        <span
                            style={{
                                width: "max-content",
                                display: "flex",
                                color: cartConfig?.cart_design?.footer_text_color,
                                fontSize: cartConfig?.cart_design?.footer_text_size,
                                fontWeight: cartConfig?.cart_design?.footer_text_style?.split(" ")[0],
                                fontStyle: cartConfig?.cart_design?.footer_text_style?.split(" ")[1],
                                alignItems: "center",
                            }}
                        >
                            {getPriceLabel((cart?.total_price || 0) / 100, money_format, currency, false)}
                            &nbsp;
                            {cartConfig?.selected_drawer_footer_layout == AvailableCartFooterLayouts.VERTICAL &&
                                cart?.original_total_price - cart?.total_price > 0 && (
                                    <RenderDiscountsBadges
                                        arr={[
                                            {
                                                title: (
                                                    <>
                                                        {cart?.total_price < cart?.original_total_price && "-"}
                                                        {getPriceLabel(
                                                            Math.abs(cart?.original_total_price - cart?.total_price) /
                                                            100,
                                                            money_format,
                                                            currency,
                                                            false,
                                                        )}
                                                    </>
                                                ),
                                            },
                                        ]}
                                        bgColor={cartConfig?.cart_design?.footer_discount_tag_bg_color}
                                        textColor={cartConfig?.cart_design?.footer_discount_tag_text_color}
                                        showIcon={false}
                                    />
                                )}
                        </span>
                        {cartFooter.showComparedPrice && cart?.original_total_price > cart?.total_price && (
                            <span
                                className={styles.subtotalAmountOriginal}
                                style={{
                                    display: "flex",
                                    width: "max-content",
                                    color: cartConfig?.cart_design?.footer_compare_at_color,
                                    fontSize: cartConfig?.cart_design?.footer_compare_at_size,
                                    fontWeight: cartConfig?.cart_design?.footer_compare_at_style?.split(" ")[0],
                                    fontStyle: cartConfig?.cart_design?.footer_compare_at_style?.split(" ")[1],
                                }}
                            >
                                {getPriceLabel((cart?.original_total_price || 0) / 100, money_format, currency, false)}
                            </span>
                        )}
                    </div>
                    <div
                        className={styles.priceBreakdownCta}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowBillSummary(true);
                        }}
                        style={{
                            color: cartConfig?.cart_design?.footer_secondary_text_color,
                            fontSize: cartConfig?.cart_design?.footer_secondary_text_size,
                            fontWeight: cartConfig?.cart_design?.footer_secondary_text_style?.split(" ")[0],
                            fontStyle: cartConfig?.cart_design?.footer_secondary_text_style?.split(" ")[1],
                        }}
                    >
                        {cartConfig?.estimated_total_text ? cartConfig?.estimated_total_text : "Estimated total"}
                    </div>
                </div>

                {cartConfig?.selected_drawer_footer_layout == AvailableCartFooterLayouts.VERTICAL &&
                    customShippingProtection && (
                        <div
                            className={styles.shippingProtectionWrapper}
                            style={{
                                background: cartConfig?.additional_product_design?.additional_product_card_bg,
                                paddingRight: 0,
                                paddingLeft: 0,
                                border: "none",
                            }}
                        >
                            <div className={styles.shippingProtectionLeft}>
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <span
                                        className={styles.shippingProtectionTitle}
                                        style={{
                                            fontSize:
                                                cartConfig?.additional_product_design?.additional_product_title_size,
                                            color: cartConfig?.additional_product_design
                                                ?.additional_product_title_color,
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                cartConfig?.additional_product?.shipping_protection_title ||
                                                "Shipping protection",
                                        }}
                                    ></span>

                                    <span
                                        className={styles.shippingProtectionSubtext}
                                        style={{
                                            fontSize:
                                                cartConfig?.additional_product_design
                                                    ?.additional_product_description_size,
                                            color: cartConfig?.additional_product_design
                                                ?.additional_product_description_color,
                                            fontWeight:
                                                cartConfig?.additional_product_design?.additional_product_description_style?.split(
                                                    " ",
                                                )[0],
                                            fontStyle:
                                                cartConfig?.additional_product_design?.additional_product_description_style?.split(
                                                    " ",
                                                )[1],
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: cartConfig?.additional_product?.shipping_protection_subtext?.replace(
                                                "{price}",
                                                money_format
                                                    .replace(
                                                        /\{\{.*?\}\}/g,
                                                        (
                                                            cartConfig?.additional_product?.shipping_protection_price *
                                                            (window.kefi_bundles.localization.rate || 1)
                                                        )?.toFixed(2),
                                                    )
                                                    ?.toString(),
                                            ),
                                        }}
                                    ></span>
                                </div>
                            </div>

                            <div className={styles.shippingProtectionRight}>
                                <span
                                    className={styles.shippingProtectionPrice}
                                    style={{
                                        fontWeight: "bold",
                                        fontSize: cartConfig?.additional_product_design?.additional_product_title_size,
                                        color: cartConfig?.additional_product_design?.additional_product_title_color,
                                    }}
                                >
                                    {money_format.replace(
                                        /\{\{.*?\}\}/g,
                                        (
                                            cartConfig?.additional_product?.shipping_protection_price *
                                            (window.kefi_bundles.localization.rate || 1)
                                        )?.toFixed(2),
                                    )}
                                </span>
                            </div>
                        </div>
                    )}
                {
                    <div
                        style={{
                            position: "relative",
                            width:
                                isMobile ||
                                    cartConfig?.selected_drawer_footer_layout == AvailableCartFooterLayouts.VERTICAL
                                    ? "100%"
                                    : "auto",
                            display: "flex",
                            flexGrow: 1,
                        }}
                    >
                        {CheckoutStyles}

                        <CheckoutButtonDisableOverlap onClick={() => {
                            setTermsError(true);
                        }} active={cartConfig?.terms_and_conditions?.status && !isTermsChecked && window.kefi_bundles.shop === "5335df-00.myshopify.com"} />
                        <Button
                            // title={cartConfig?.checkout_cta_text}
                            title={
                                <div>
                                    <div>
                                        <div>
                                            {parseCTAWithIcon(
                                                cartConfig?.checkout_cta_text || "",
                                                cartConfig?.checkout_icon_url,
                                                cartConfig.cart_design?.footer_button_cta_text_size + 8,
                                            )}
                                        </div>

                                        {/* Conditional extra text */}
                                        {/* {customShippingProtection && (
                                            <div
                                                className={styles.checkoutButtonShippingProtection}
                                                style={{
                                                    marginTop: 4,
                                                    fontSize:
                                                        cartConfig.cart_design?.footer_button_cta_text_size * 0.55,
                                                    fontWeight: 400,
                                                    opacity: 0.9,
                                                    "--buttonHoverText":
                                                        cartConfig?.cart_design?.footer_button_text_hover_color,
                                                }}
                                                dangerouslySetInnerHTML={{
                                                    __html: "With {price} Shipping Protection".replace(
                                                        "{price}",
                                                        money_format.replace(
                                                            /\{\{.*?\}\}/g,
                                                            (
                                                                cartConfig?.additional_product
                                                                    ?.shipping_protection_price *
                                                                (window.kefi_bundles.localization.rate || 1)
                                                            )?.toFixed(2),
                                                        ),
                                                    ),
                                                }}
                                            />
                                        )} */}
                                    </div>

                                    {/* Right side */}
                                    {customShippingProtection && <div className={styles.checkoutButtonArrow}>›</div>}
                                </div>
                            }
                            className={`${styles.checkoutButton} ${styles.checkoutButtonHover} kefiCartCheckoutButton`}
                            id={"kefiCheckoutButton"}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (cartConfig?.terms_and_conditions?.status && !isTermsChecked) {
                                    setTermsError(true);
                                    return;
                                }

                                if (standAlone) {
                                    updateBundleStats({
                                        id: null,
                                        key: "clicks",
                                        shop: window.kefi_bundles?.shop,
                                        appName: config.appName,
                                        view_type: 2,
                                    });
                                    const isShippingProtectionOn = window.sessionStorage.getItem("ap_status") === "on";

                                    if (
                                        (customShippingProtection ||
                                            (isShippingProtectionOn && cartConfig?.additional_product?.status)) &&
                                        currentProduct
                                    ) {
                                        setIsUpdating(true);
                                        const lineIndex = cart?.items?.findIndex(
                                            (pd) => !!currentProduct?.id?.includes(pd.product_id as unknown as string),
                                        );

                                        if (lineIndex >= 0) {
                                            // Already present  then just checkout
                                            handleCheckout(cartConfig?.cart_settings?.redirect_to_checkout);
                                        } else {
                                            // Not in cart add then checkout
                                            handleAdditionalProductAdd({
                                                state: true,
                                                additionalProduct: {
                                                    ...currentProduct,
                                                    selectedVariants: [currentProduct.variants[0]],
                                                },
                                                checkoutWithShipping: true,
                                            });
                                        }
                                    } else {
                                        handleCheckout(cartConfig?.cart_settings?.redirect_to_checkout);
                                    }
                                }
                            }}
                            style={{
                                flexGrow: 1,
                                width: "100%",
                                fontSize: cartConfig.cart_design?.footer_button_cta_text_size,
                                fontWeight: cartConfig?.cart_design?.footer_button_cta_text_style?.split(" ")[0],
                                fontStyle: cartConfig?.cart_design?.footer_button_cta_text_style?.split(" ")[1],
                                borderRadius: cartConfig?.cart_design?.footer_button_radius,
                                // @ts-expect-error hack
                                "--buttonBg": cartConfig?.cart_design?.footer_button_bg_color,
                                "--buttonText": cartConfig?.cart_design?.footer_button_text_color,
                                "--buttonHoverBg": cartConfig?.cart_design?.footer_button_bg_hover_color,
                                "--buttonHoverText": cartConfig?.cart_design?.footer_button_text_hover_color,
                                "--buttonBgBorder": cartConfig?.cart_design?.footer_button_border_color,
                                "--buttonHoverBgBorder": cartConfig?.cart_design?.footer_button_hover_border_color,
                                padding: cartConfig?.checkout_cta_text?.includes("{icon}") ? 8 : 10,
                            }}
                        />

                        {cartConfig?.cart_settings?.show_text_below_add_to_cart && (
                            <>
                                <span
                                    id={"kcdFooterMsg"}
                                    style={{
                                        position: "absolute",
                                        textAlign: "center",
                                        fontSize: 12,
                                        fontWeight: 350,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        transform: "translateY(110%)",
                                    }}
                                >
                                    {cartConfig?.cart_settings?.text_below_add_to_cart}
                                </span>
                            </>
                        )}
                    </div>
                }
            </div>

            {/* user specific shipping button */}
            <ShippingProtectionCustomButton
                cartConfig={cartConfig}
                handleAdditionalProductAdd={handleAdditionalProductAdd}
                standAlone={standAlone}
                cartLines={cart?.items}
                handleCheckout={handleCheckout}
                currentProduct={currentProduct}
                customShippingProtection={customShippingProtection}
            />

            <CustomHTMLWrapper cartConfig={cartConfig} currentPosition={CUSTOM_HTML_POS_OPT.BELOW_CHECKOUT} />

            {(cartConfig?.trust_badges?.status || (!standAlone && cartConfig?.trust_badges?.status)) && (
                <div className={styles.trustBadgesWrapper}>
                    {selectedBadges?.length ? (
                        selectedBadges.map((badge, idx) => {
                            return (
                                <div key={badge?.originalSrc + idx} className={styles.trustBadge}>
                                    <img
                                        src={badge?.originalSrc}
                                        alt={badge?.originalSrc}
                                        style={{
                                            width: cartConfig?.trust_badges?.badge_size + "px",
                                            objectFit: "contain",
                                            objectPosition: "center",
                                        }}
                                    />
                                </div>
                            );
                        })
                    ) : (
                        <div className={styles.emptyBadges}>Please Select Badges</div>
                    )}
                </div>
            )}

            <CustomHTMLWrapper cartConfig={cartConfig} currentPosition={CUSTOM_HTML_POS_OPT.BELOW_BADGES} />

            {cartConfig?.cart_settings.show_continue_shopping_cta && (
                <span
                    className={styles.continueShopping}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowCartDrawer(false);
                    }}
                    style={{
                        color: cartConfig?.cart_design?.footer_secondary_text_color,
                    }}
                >
                    {cartConfig?.continue_shop_cta_text}
                </span>
            )}
            {cartConfig?.cart_settings.show_cart_cta && (
                <span
                    className={styles.continueShopping}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window.location = window.kefi_bundles?.routes?.cart_url as unknown as Location;
                    }}
                    style={{
                        color: cartConfig?.cart_design?.footer_secondary_text_color,
                    }}
                >
                    {cartConfig?.view_cart_cta_text}
                </span>
            )}

            {showDiscountBox && (
                <DiscountBox
                    cartConfig={cartConfig}
                    handler={setShowDiscountBox}
                    state={showDiscountBox}
                    sft={sft}
                    cart={cart}
                    standAlone={!!standAlone}
                    setErrors={setErrors}
                    handleFetchCart={handleFetchCart}
                    discountApplied={discountApplied}
                    setState={setShowDiscountBox}
                />
            )}
            {showAdditionalNote && (
                <AdditionalNoteBox
                    cart={cart}
                    cartConfig={cartConfig}
                    handler={setShowAdditionalNote}
                    state={showAdditionalNote}
                    updateCartData={updateCartData}
                />
            )}
            {addProductToCart?.id && (
                <AddProductToCartBox
                    setVariant={setAddProductToCart}
                    product={addProductToCart}
                    cartConfig={cartConfig}
                    currency={currency}
                    money_format={money_format}
                    addToCartFromCart={addToCartFromCart}
                    cart={cart}
                    standAlone={standAlone}
                    isRewardFreeGift={false}
                />
            )}
            {showBillSummary && (
                <BillSummaryBox
                    handler={setShowBillSummary}
                    state={showBillSummary}
                    money_format={money_format}
                    currency={currency}
                    cart={cart}
                    discountApplied={discountApplied}
                    cartConfig={cartConfig}
                />
            )}
            {/* <PoweredByKefiBranding
                bundleConfig={{ general_settings: { show_powered_by_kefi: cartConfig?.show_kefi_branding } }}
                _style={{ marginTop: -1 }}
            /> */}
        </div>
    );
};

export default CartFooter;
